// This file contains utility functions for data processing, formatting, and
// geospatial calculations used throughout the CEA Screening Tool UI.

import * as turf from "@turf/turf";

/* Example Usage:
const facilityData = {
  // ... (facility data)
};

const reductionData = [
  // ... (reduction data)
];
const results = applyPercentageReductionsPerPathway(facilityData, reductionData);
console.log(results);
 */

// Applies percent reductions for each decarbonization pathway to the given
// facility data, returning an array of results for each pathway.
export function applyPercentReductionsPerPathway(facilityData, reductionData) {
  const results = reductionData.map(pathway => {
    const {
      "Energy (% Reduction Min)": energyMin,
      "Energy (% Reduction Max)": energyMax,
      "Water (% Reduction Min)": waterMin,
      "Water (% Reduction Max)": waterMax,
      "Emissions (% Reduction Min)": emissionsMin,
      "Emissions (% Reduction Max)": emissionsMax
    } = pathway;

    let minElectricity = facilityData["Electricity Required for LEDs (MWh/year)"];
    let maxElectricity = facilityData["Electricity Required for LEDs (MWh/year)"];
    let minEnergyHeatingCooling = facilityData["Energy Required for Heating and Cooling (MWh/year)"];
    let maxEnergyHeatingCooling = facilityData["Energy Required for Heating and Cooling (MWh/year)"];
    let minEnergyVentilation = facilityData["Energy Required for Ventilation (MWh/year)"];
    let maxEnergyVentilation = facilityData["Energy Required for Ventilation (MWh/year)"];
    let minAnnualEnergyConsumption = facilityData["Annual Energy Consumption (MWh/year)"];
    let maxAnnualEnergyConsumption = facilityData["Annual Energy Consumption (MWh/year)"];
    let minWaterUse = facilityData["Annual Water Consumption (L/year)"];
    let maxWaterUse = facilityData["Annual Water Consumption (L/year)"];
    let minCO2 = facilityData["CO2 emissions (kg/year)"];
    let maxCO2 = facilityData["CO2 emissions (kg/year)"];

    // Apply energy reductions if specified.
    if (energyMin !== null) {
      if (minElectricity !== null) {
        minElectricity *= (1 - energyMax / 100);
        maxElectricity *= (1 - energyMin / 100);
      }
      if (minEnergyHeatingCooling !== null) {
        minEnergyHeatingCooling *= (1 - energyMax / 100);
        maxEnergyHeatingCooling *= (1 - energyMin / 100);
      }
      if (minEnergyVentilation !== null) {
        minEnergyVentilation *= (1 - energyMax / 100);
        maxEnergyVentilation *= (1 - energyMin / 100);
      }
      if (minAnnualEnergyConsumption !== null) {
        minAnnualEnergyConsumption *= (1 - energyMax / 100);
        maxAnnualEnergyConsumption *= (1 - energyMin / 100);
      }
    }

    // Apply water reductions if specified.
    if (waterMin !== null && minWaterUse !== null) {
      minWaterUse *= (1 - waterMax / 100);
      maxWaterUse *= (1 - waterMin / 100);
    }

    // Apply emissions reductions if specified.
    if (emissionsMin !== null && minCO2 !== null) {
      minCO2 *= (1 - emissionsMax / 100);
      maxCO2 *= (1 - emissionsMin / 100);
    }

    // Helper function to safely call toFixed or return 'N/A'.
    const safeToFixed = (value, digits = 0) =>
      (typeof value === 'number' && !isNaN(value)) ? value.toFixed(digits) : 'N/A';

    return {
      "id": pathway.id,
      "Pathway": pathway.Pathway,
      "Electricity Required for LEDs (MWh/year) Min": safeToFixed(minElectricity, 0),
      "Electricity Required for LEDs (MWh/year) Max": safeToFixed(maxElectricity, 0),
      "Energy Required for Heating and Cooling (MWh/year) Min": safeToFixed(minEnergyHeatingCooling, 0),
      "Energy Required for Heating and Cooling (MWh/year) Max": safeToFixed(maxEnergyHeatingCooling, 0),
      "Energy Required for Ventilation (MWh/year) Min": safeToFixed(minEnergyVentilation, 0),
      "Energy Required for Ventilation (MWh/year) Max": safeToFixed(maxEnergyVentilation, 0),
      "Annual Energy Consumption (MWh/year) Min": safeToFixed(minAnnualEnergyConsumption, 0),
      "Annual Energy Consumption (MWh/year) Max": safeToFixed(maxAnnualEnergyConsumption, 0),
      "Annual Water Consumption (L/year) Min": safeToFixed(minWaterUse, 0),
      "Annual Water Consumption (L/year) Max": safeToFixed(maxWaterUse, 0),
      "CO2 emissions (kg/year) Min": safeToFixed(minCO2, 0),
      "CO2 emissions (kg/year) Max": safeToFixed(maxCO2, 0)
    };
  });

  return results;
}

// Removes a key from an object, returning a new object without modifying the original.
export function removeItemFromObject(obj, keyToRemove) {
  // Create a shallow copy of the object to avoid modifying the original.
  const newObj = { ...obj };

  // Check if the key exists before attempting to delete it.
  if (newObj.hasOwnProperty(keyToRemove)) {
    delete newObj[keyToRemove];
  }

  return newObj;
}

// Finds the nearest facility in jsonData to the given latitude/longitude
// that matches the specified type, size, tech, and crop.
export function findNearestFacility(
    latitude, longitude, type, size, tech, crop, jsonData
) {
    // Create a Turf.js point for the target location
    const targetPoint = turf.point(
        [parseFloat(longitude), parseFloat(latitude)]
    );

    let nearestEntry = null;
    let minDistance = Infinity;

    jsonData.forEach((entry) => {
        // First check that facility meets parameters set in UI
        if (
            entry['Facility Type'] === type &&
            entry['Facility Size'] === size &&
            entry['Crop'] === crop &&
            entry['Technology Level'] === tech
        ) {
            // Create a Turf.js point for the current entry's location
            const entryPoint = turf.point(
                [parseFloat(entry.Longitude), parseFloat(entry.Latitude)]);

            // Calculate the distance between the target point and the
            // entry point using Turf.js
            const distance = turf.distance(targetPoint, entryPoint);

            if (distance < minDistance) {
                minDistance = distance;
                nearestEntry = entry;
            }
        }
    });

    return nearestEntry;
}

// Returns true if the string contains any letters (a-z or A-Z).
export function hasLetters(str) {
    return /[a-zA-Z]/.test(str);
}

/**
 * Rounds a number to a specified number of significant figures.
 *
 * @param {number|string} num The number to round (can be a numerical string).
 * @param {number} [x=2] The desired number of significant figures. Defaults to 2.
 * @param {any} [zeroValueReturn=0] The value to return if num is 0. Defaults to 0.
 * @returns {number|any} The rounded number, or the specified zeroValueReturn if num is 0.
 * @throws {TypeError} If the input string contains non-numerical characters (excluding minus sign).
 */
export function roundToSigFigs(num, x = 2, zeroValueReturn = 0) {
  // Handle the case where the input is a string.
  if (typeof num === 'string') {
    // Check if the string represents a valid number (including negative numbers).
    const numericRegex = /^-?\d*\.?\d+$/; // Regex to validate numeric strings
    if (numericRegex.test(num)) {
      // Convert the string to a floating-point number.
      num = parseFloat(num);
    } else {
      // If the string is not a valid number (e.g., "N/A"), return it as is.
      return num;
    }
  } else if (typeof num !== 'number') {
      // If the input is not a number or a string, return it as is.
      return num;
  }

  // Handle the special case where the number is zero.
  // Zero has no significant figures in the traditional sense.
  // We return the value provided in the zeroValueReturn parameter.
  if (num === 0) {
    return zeroValueReturn;
  }

  // Calculate the magnitude (power of 10) of the number.
  // This determines the position of the leftmost significant digit.
  // Math.abs() is used to handle negative numbers correctly.
  // Math.log10() calculates the base-10 logarithm.
  // Math.floor() rounds the logarithm down to the nearest integer.
  const magnitude = Math.floor(Math.log10(Math.abs(num)));

  // Calculate the shift needed to position the significant digits for rounding.
  //  x - 1:  Adjust for the fact that 'magnitude' refers to the power of the *first* digit.
  // -magnitude: Adjust for the decimal place of the number.
  const shift = x - 1 - magnitude;

  // Perform the rounding operation.
  // num * Math.pow(10, shift): Shifts the decimal point to the right,
  //                             placing the desired significant digits to the left of the decimal.
  // Math.round(): Rounds the shifted number to the nearest integer.
  // / Math.pow(10, shift): Shifts the decimal point back to its original position.
  const rounded = Math.round(num * Math.pow(10, shift)) / Math.pow(10, shift);

  // Return the rounded number.
  return rounded;
}

// Formats a number with commas as thousands separators.
export function numWithCommas(x, d = 0) {
    return parseFloat(x).toFixed(d).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Formats a number as USD currency with commas and two decimal places.
export function numWithCommasUSD(x, d = 2) {
    return '$' + numWithCommas(parseFloat(x).toFixed(d), d)
}

// Converts a string to title case (first letter of each word capitalized).
export function toTitleCase(str) {
    return str.replace(
        /\w\S*/g,
        text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
    );
}

// Returns a random integer between min (inclusive) and max (exclusive).
export function getRandomInt(min, max) {
    const minCeiled = Math.ceil(min);
    const maxFloored = Math.floor(max);
    return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled); // The maximum is exclusive and the minimum is inclusive
}

// Returns a timestamp string in MM-DD-YYYY-HHMMSS format.
export function getTimestamp() {
    const pad = (n, s = 2) => (`${new Array(s).fill(0)}${n}`).slice(-s);
    const d = new Date();
    var rv = `${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getFullYear(), 4)}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    rv = rv.replaceAll(' ', '_').replaceAll(':', '.')
    return rv
}

// Converts a state FIPS code to the corresponding state name.
export function stateFpToName(stateFp) {
    const stateFpToNameDict = {
        '01': 'Alabama',
        '02': 'Alaska',
        '04': 'Arizona',
        '05': 'Arkansas',
        '06': 'California',
        '08': 'Colorado',
        '09': 'Connecticut',
        '10': 'Delaware',
        '12': 'Florida',
        '13': 'Georgia',
        '15': 'Hawaii',
        '16': 'Idaho',
        '17': 'Illinois',
        '18': 'Indiana',
        '19': 'Iowa',
        '20': 'Kansas',
        '21': 'Kentucky',
        '22': 'Louisiana',
        '23': 'Maine',
        '24': 'Maryland',
        '25': 'Massachusetts',
        '26': 'Michigan',
        '27': 'Minnesota',
        '28': 'Mississippi',
        '29': 'Missouri',
        '30': 'Montana',
        '31': 'Nebraska',
        '32': 'Nevada',
        '33': 'New Hampshire',
        '34': 'New Jersey',
        '35': 'New Mexico',
        '36': 'New York',
        '37': 'North Carolina',
        '38': 'North Dakota',
        '39': 'Ohio',
        '40': 'Oklahoma',
        '41': 'Oregon',
        '42': 'Pennsylvania',
        '44': 'Rhode Island',
        '45': 'South Carolina',
        '46': 'South Dakota',
        '47': 'Tennessee',
        '48': 'Texas',
        '49': 'Utah',
        '50': 'Vermont',
        '51': 'Virginia',
        '53': 'Washington',
        '54': 'West Virginia',
        '55': 'Wisconsin',
        '56': 'Wyoming',
        '60': 'American Samoa',
        '66': 'Guam',
        '69': 'Northern Mariana Islands',
        '72': 'Puerto Rico',
        '78': 'Virgin Islands'
    }
    return (stateFpToNameDict[stateFp])
}
